// Where you came from, inside the OS — the memory behind "← Back to …".
//
// Grant, 2026-10-13: "navigating back to certain places in the OS is
// annoying." The record headers said "← Opportunities" whatever you had
// actually been doing, so opening an opportunity from a household and
// clicking back dropped you on the Opportunities list, filters gone, and
// you had to find the household again. This provider remembers the trail
// of pages you visited in THIS tab so a back link can name the page you
// really came from and return you to it exactly as you left it (same
// filters, same scroll) via the browser's own history.
//
// DECISION: memory only, never sessionStorage/localStorage. The labels it
// keeps are record names ("Smith Household"), i.e. client names, and the
// mobile-parity rule keeps client PII out of phone storage. The cost is
// that a full page reload forgets where you came from; every back link
// then falls back to its parent page, which is what it did before.
//
// DECISION: we only ever go back through history (navigate(-steps)), never
// by pushing the previous URL. Pushing would make the browser's own Back
// button walk forward again, so the two "backs" would disagree.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigationType } from "react-router-dom";

import { labelForPreviousPage, type Crumb } from "./breadcrumb-helpers";

interface VisitedPage {
  /** react-router's per-history-entry key. */
  key: string;
  pathname: string;
  /** pathname + search, i.e. where "back" lands (filters included). */
  href: string;
}

export interface PreviousPage {
  href: string;
  pathname: string;
  /** How many history entries back it is (1 unless the current page
   *  changed only its own tab or filter in between). */
  steps: number;
  /** Plain words for the link: "Smith Household", "Integrations". */
  label: string;
}

interface NavHistoryApi {
  previous: PreviousPage | null;
  /** The current page's own name, for the breadcrumb's record step. */
  currentName: string | null;
  /** A page-supplied trail that replaces the URL-derived one. */
  pageTrail: Crumb[] | null;
  /** How many smart back links the current page renders itself. */
  ownBackLinks: number;
  setName: (key: string, name: string | null) => void;
  setTrail: (key: string, trail: Crumb[] | null) => void;
  addOwnBackLink: () => () => void;
}

const NavHistoryContext = createContext<NavHistoryApi | null>(null);

export function NavHistoryProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const stackRef = useRef<VisitedPage[]>([]);
  const indexRef = useRef(-1);
  // Bumped whenever the stack changes, so consumers re-render.
  const [version, setVersion] = useState(0);
  // Names and trails are keyed by the visit (location.key), not stored on
  // the stack entry: React runs a page's effects BEFORE this provider's,
  // so a page registers its name before the visit above is even recorded.
  const namesRef = useRef(new Map<string, string>());
  const [pageTrail, setPageTrail] = useState<{ key: string; trail: Crumb[] } | null>(null);
  const [ownBackLinks, setOwnBackLinks] = useState(0);

  useEffect(() => {
    const entry: VisitedPage = {
      key: location.key,
      pathname: location.pathname,
      href: location.pathname + location.search,
    };
    const stack = stackRef.current;
    const at = stack.findIndex((p) => p.key === location.key);
    if (navigationType === "POP" && at !== -1) {
      // Back/forward to a page we already know: just move the pointer.
      indexRef.current = at;
    } else if (navigationType === "REPLACE" && indexRef.current >= 0) {
      stack[indexRef.current] = entry;
    } else {
      // A new page (or a POP to an entry from before this tab's memory
      // began). Anything "forward" of here is no longer reachable.
      stack.splice(indexRef.current + 1);
      stack.push(entry);
      indexRef.current = stack.length - 1;
    }
    setVersion((v) => v + 1);
  }, [location.key, location.pathname, location.search, navigationType]);

  const setName = useCallback((key: string, name: string | null) => {
    const names = namesRef.current;
    if ((names.get(key) ?? null) === name) return;
    if (name) names.set(key, name);
    else names.delete(key);
    setVersion((v) => v + 1);
  }, []);

  const setTrail = useCallback((key: string, trail: Crumb[] | null) => {
    setPageTrail((cur) => {
      if (trail) return { key, trail };
      // Only clear the trail this page set; the next page may already
      // have put its own in place.
      return cur?.key === key ? null : cur;
    });
  }, []);

  const addOwnBackLink = useCallback(() => {
    setOwnBackLinks((n) => n + 1);
    return () => setOwnBackLinks((n) => n - 1);
  }, []);

  const api = useMemo<NavHistoryApi>(() => {
    const stack = stackRef.current;
    const idx = indexRef.current;
    const current = idx >= 0 ? stack[idx] : undefined;
    // Only trust the stack when its pointer really is on this page; on
    // the very first render (before the effect above has run) it isn't.
    const synced = current?.key === location.key;
    // The page before this one is the nearest earlier entry on a
    // DIFFERENT page: a tab or filter change on this same page (which
    // can add history entries of its own) is not somewhere to go back to.
    let priorIdx = synced ? idx - 1 : -1;
    while (priorIdx >= 0 && stack[priorIdx]?.pathname === current?.pathname) priorIdx -= 1;
    const prior = priorIdx >= 0 ? stack[priorIdx] : undefined;
    const previous =
      prior && current
        ? {
            href: prior.href,
            pathname: prior.pathname,
            steps: idx - priorIdx,
            label: labelForPreviousPage(prior.pathname, namesRef.current.get(prior.key)),
          }
        : null;
    return {
      previous,
      currentName: namesRef.current.get(location.key) ?? null,
      // A trail left over from the page you just left is never shown.
      pageTrail: pageTrail && pageTrail.key === location.key ? pageTrail.trail : null,
      ownBackLinks,
      setName,
      setTrail,
      addOwnBackLink,
    };
    // `version` is the signal that the (mutable) stack changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, location.key, pageTrail, ownBackLinks, setName, setTrail, addOwnBackLink]);

  return <NavHistoryContext.Provider value={api}>{children}</NavHistoryContext.Provider>;
}

/** Null outside the provider (stories, isolated tests): callers degrade
 *  to their plain parent link. */
export function useNavHistory(): NavHistoryApi | null {
  return useContext(NavHistoryContext);
}

/**
 * A page tells the OS what to call it — normally the record it shows
 * ("Smith Household", "Home Policy · Smith"). That name replaces "Detail"
 * in the breadcrumb, and becomes the label of the "← Back to …" link on
 * whatever page you open from here.
 */
export function usePageName(name: string | null | undefined): void {
  const history = useNavHistory();
  const { key } = useLocation();
  const setName = history?.setName;
  useEffect(() => {
    if (!setName) return;
    const trimmed = name?.trim() || null;
    setName(key, trimmed);
  }, [setName, key, name]);
}

/**
 * A page that knows a better trail than its web address (a contact sits
 * under its household, not under "Contacts") supplies it here and the
 * one trail at the top of the screen shows it. Pass the steps AFTER
 * "Home"; the last step is the current page. Memoise the array.
 */
export function usePageTrail(trail: Crumb[] | null): void {
  const history = useNavHistory();
  const setTrail = history?.setTrail;
  const setName = history?.setName;
  const { key } = useLocation();
  useEffect(() => {
    if (!setTrail) return;
    setTrail(key, trail);
    const last = trail?.at(-1)?.label ?? null;
    setName?.(key, last);
    return () => setTrail(key, null);
  }, [setTrail, setName, key, trail]);
}
